/* eslint-disable @nx/enforce-module-boundaries */
import { provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  isDevMode,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withDebugTracing,
  withRouterConfig,
} from '@angular/router';
import * as AuthStore from '@kanzi-apes/kanzi-auth';
import { KanziUtilsModule } from '@kanzi-apes/kanzi-utils';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { environment } from '../environments/environment';
import * as LayoutReducer from './+state/layout/layout.reducer';
import { metaReducers, reducers } from './+state/reducers';
import { appRoutes } from './app.routes';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  provideAngularQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { ConfirmationService } from 'primeng/api';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { provideServiceWorker } from '@angular/service-worker';
import { JoyrideModule } from 'ngx-joyride';

const socketUrl = environment.socketIOServer || '';
// If the URL contains /nodered/, extract that path and use it
const socketPath = socketUrl.includes('/nodered/')
  ? '/nodered/socket.io/'
  : '/socket.io/';

const config: SocketIoConfig = {
  url: socketUrl.replace('/nodered/', ''),
  options: {
    path: socketPath,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 10,
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withDebugTracing(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideAnimations(),
    provideHttpClient(),
    importProvidersFrom(KanziUtilsModule.forRoot({ env: environment })),
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      })
    ),
    importProvidersFrom(
      StoreDevtoolsModule.instrument({
        name: 'Kanzi RFID DevTools',
        logOnly: environment.production,
        maxAge: 30,
        connectInZone: true,
      })
    ),
    importProvidersFrom(EffectsModule.forRoot([])),
    importProvidersFrom(KanziUtilsModule.forRoot({ env: environment })),
    importProvidersFrom(
      StoreModule.forFeature(
        LayoutReducer.LAYOUT_FEATURE_KEY,
        LayoutReducer.layoutReducer
      )
    ),
    importProvidersFrom(
      StoreModule.forFeature(AuthStore.AUTH_FEATURE_KEY, AuthStore.authReducer)
    ),
    importProvidersFrom(EffectsModule.forFeature(AuthStore.AuthEffects)),
    provideTranslateService({
      loader: provideTranslateHttpLoader(),
      fallbackLang: 'es',
      lang: 'es',
    }),
    provideAngularQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutos - datos considerados frescos
            gcTime: 1000 * 60 * 30, // 30 minutos - tiempo en cache
            refetchOnWindowFocus: false, // No refetch al volver al tab
            retry: 1, // Reintentar una vez en caso de error
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      })
    ),
    importProvidersFrom(SocketIoModule.forRoot(config)),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    importProvidersFrom(JoyrideModule.forRoot()),
  ],
};
